/** @module store/users */

import * as actions from '@/store/modules/users/actions'
import * as getters from '@/store/modules/users/getters'
import * as mutations from '@/store/modules/users/mutations'

/**
 * Store state for current `users`.
 *
 * - {object} user, defined by at least a `{integer} id` and a `{string} email`
 *
 * state.users[userId] => {object} includes all the User information for the User with the ID userId, e.g email, roles
 *
 * state.emss.direct[emsId] => {object} mapping `[userId] => {Permission}`.
 * Saves the directly related Users with their permission-flag per role (ctxt) for an EMS
 *
 * state.emss.throughGroups[emsId] => {object} mapping [userId] => {emsGroupIds: Array[Integer], customer: Integer, ...}.
 * Saves the `{Permission}` for the User through emsGroups, extended by the related emsGroupIds
 *
 * state.emsGroups[emsGroupId] => `{object}` mapping `[userId] => {Permission}`.
 * Saves the users for a given emsGroup with their permission-flag per role (ctxt).
 *
 * @function
 *
 * @return {object}
 */
export const state = () => ({
  emss: {
    direct: {},
    throughGroups: {}
  },
  emsGroups: {},
  users: {}
})

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}

export const CTXT_TO_ROLE_MAP = {
  as_admin: 'admin',
  as_customer: 'customer',
  as_operator: 'operator',
  as_technician: 'technician'
}

export const ROLE_TO_CTXT_MAP = {}
for (const ctxt in CTXT_TO_ROLE_MAP) {
  ROLE_TO_CTXT_MAP[CTXT_TO_ROLE_MAP[ctxt]] = ctxt
}

export const SUPPORTED_PERM_ROLES_FOR_VIEW = ['admin', 'customer', 'technician'] // `'operator'` is currently patched out

export const SUPPORTED_ROLES_FOR_VIEW = ['admin', 'customer', 'technician', 'demo', 'mydemo'] // `'operator'` is currently patched out
export const SUPPORTED_SUDO_ROLES_FOR_VIEW = ['org_sudo', 'sudo', 'tester']

/**
 * As defined by the BE.
 *
 * @const
 *
 */
export const PERMISSION_FLAGS = {
  none: 0,
  create: 1,
  read: 2,
  update: 4,
  delete: 8,
  demo: 16,
  orgRead: 32,
  orgWrite: 64
}

export const EMS_PERMISSION_TYPES = ['read', 'create', 'update', 'delete', 'demo']

export const DIRECT_EMS_PERMISSION_TYPES = ['read', 'create', 'update', 'delete', 'orgAdmin', 'demo']

export const ORG_PERMISSION_TYPES = ['orgRead', 'orgWrite']

/**
 * A User data-struct.
 *
 * @constructor
 *
 * @param {object} params
 * @param {string|integer} params.id is the user's ID
 * @param {string} params.email is the user's email (required)
 * @param {array} params.roles is a list of user's roles
 *
 */
export function User({ id, email, roles, certifiedPartnerIdentification }) {
  if (!email) {
    throw TypeError(`Missing or invalid email ${email}.`)
  }

  this.id = parseInt(id)
  this.email = email
  this.roles = roles

  if (certifiedPartnerIdentification) this.certifiedPartnerIdentification = certifiedPartnerIdentification
}

/**
 * A permission data-struct.
 * For each user's role the permission flag (integer) is stored.
 * E.g. `{ admin: undefined, customer: 6, ... }`
 *
 * @constructor
 *
 * @param {object} permission is the data-struct of the initial permission. It equivalently accepts permission-flag(s) under a context or role key. E.g `{ as_customer: 4 }`, and `{ customer: 4 }` is handled.
 * @param {object} params
 * @param {boolean} params.none allows to init permission with `0` flag, iff initial permission is not present
 *
 */
export function Permission(permission = {}, { none = false, defaultFlag = null } = {}) {
  for (const ctxt in CTXT_TO_ROLE_MAP) {
    const role = CTXT_TO_ROLE_MAP[ctxt]
    this[role] = permission[ctxt]
    if (this[role] === undefined) {
      this[role] = permission[role]
      if (none && !this[role]) {
        this[role] = 0
      }
      if (defaultFlag && !this[role]) {
        this[role] = defaultFlag
      }
    }
  }
}

export function permissionPerTypeFromFlag(permFlag) {
  if (!permFlag) {
    permFlag = 0
  }

  return {
    create: (permFlag & PERMISSION_FLAGS.create) > 0,
    read: (permFlag & PERMISSION_FLAGS.read) > 0,
    update: (permFlag & PERMISSION_FLAGS.update) > 0,
    delete: (permFlag & PERMISSION_FLAGS.delete) > 0,
    demo: (permFlag & PERMISSION_FLAGS.demo) > 0,
    orgRead: (permFlag & PERMISSION_FLAGS.orgRead) > 0,
    orgWrite: (permFlag & PERMISSION_FLAGS.orgWrite) > 0,
    orgAdmin: (permFlag & (PERMISSION_FLAGS.orgRead + PERMISSION_FLAGS.orgWrite)) > 0
  }
}

/**
 * Allows to create a Permission with all roles (ctxts) having flag-permission for org-read and -write.
 *
 * @function
 *
 * @return {Permission}
 */
export function createFullOrgPermission() {
  let defaultFlag = 0
  ORG_PERMISSION_TYPES.forEach((t) => {
    defaultFlag = defaultFlag | PERMISSION_FLAGS[t]
  })

  return new Permission({}, { defaultFlag })
}

/**
 * Allows to create a Permission with all roles (ctxts) having flag-permission for EMS `rw + demo`.
 *
 * @function
 *
 * @return {Permission}
 */
export function createFullEmsPermission() {
  let defaultFlag = 0
  EMS_PERMISSION_TYPES.forEach((t) => {
    defaultFlag = defaultFlag | PERMISSION_FLAGS[t]
  })

  return new Permission({}, { defaultFlag })
}
